import type { FC, ReactNode } from 'react'

import { useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonClassName } from '@/components/generals/button'
import { ConsortiumIcon } from '@/components/landing/consortium-icon'
import { consortiumCategories } from '@/constants/consorcios'
import { mainNavigation } from '@/constants/navigation'

interface SiteHeaderProps {
    ctaHref: string
}

const navLinkClassName = 'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-foreground/75 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

export const SiteHeader: FC<SiteHeaderProps> = ({ ctaHref }): ReactNode => {
    const router = useRouter()
    const productsPanelId = useId()
    const productsRef = useRef<HTMLDivElement>(null)
    const productsButtonRef = useRef<HTMLButtonElement>(null)
    const [isScrolled, setIsScrolled] = useState(false)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isProductsOpen, setIsProductsOpen] = useState(false)

    const closeAll = (): void => {
        setIsMenuOpen(false)
        setIsProductsOpen(false)
    }

    useEffect(() => {
        const update = (): void => setIsScrolled(window.scrollY > 8)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    useEffect(() => {
        const close = (): void => {
            setIsMenuOpen(false)
            setIsProductsOpen(false)
        }

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!isMenuOpen && !isProductsOpen) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            if (isProductsOpen) productsButtonRef.current?.focus()

            setIsMenuOpen(false)
            setIsProductsOpen(false)
        }

        const onPointerDown = (event: PointerEvent): void => {
            if (productsRef.current && !productsRef.current.contains(event.target as Node)) setIsProductsOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('pointerdown', onPointerDown)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('pointerdown', onPointerDown)
        }
    }, [isMenuOpen, isProductsOpen])

    const isSolid = isScrolled || isMenuOpen

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b transition-colors duration-300',
                isSolid ? 'border-border bg-background/90 backdrop-blur-md' : 'border-transparent bg-background/0'
            )}
        >
            <div className='mx-auto flex h-18 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <Link
                    href='/'
                    aria-label='Chave Consórcios, página inicial'
                    onClick={closeAll}
                    className='rounded-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                >
                    <Logo />
                </Link>

                <nav aria-label='Navegação principal' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        <li>
                            <div ref={productsRef} className='relative'>
                                <button
                                    ref={productsButtonRef}
                                    type='button'
                                    aria-expanded={isProductsOpen}
                                    aria-controls={productsPanelId}
                                    onClick={() => setIsProductsOpen(open => !open)}
                                    className={cn(navLinkClassName, isProductsOpen && 'bg-secondary text-foreground')}
                                >
                                    Consórcios
                                    <ChevronDown
                                        aria-hidden='true'
                                        className={cn('size-4 transition-transform duration-200', isProductsOpen && 'rotate-180')}
                                    />
                                </button>

                                {isProductsOpen && (
                                    <div
                                        id={productsPanelId}
                                        className='absolute left-0 top-full mt-3 w-[36rem] rounded-2xl border border-border bg-popover p-3 shadow-xl shadow-ink/10 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-200'
                                    >
                                        <ul className='grid grid-cols-2 gap-1'>
                                            {consortiumCategories.map(category => (
                                                <li key={category.slug}>
                                                    <Link
                                                        href={`/consorcio/${category.slug}`}
                                                        onClick={closeAll}
                                                        className='flex h-full items-start gap-3 rounded-xl p-3 transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                                                    >
                                                        <span className='grid size-10 shrink-0 place-items-center rounded-lg bg-sand text-bronze'>
                                                            <ConsortiumIcon slug={category.slug} className='size-5' />
                                                        </span>

                                                        <span>
                                                            <span className='block text-sm font-semibold text-foreground'>
                                                                {category.title}
                                                            </span>

                                                            <span className='mt-0.5 block text-xs leading-snug text-muted-foreground'>
                                                                {category.tagline}
                                                            </span>
                                                        </span>
                                                    </Link>
                                                </li>
                                            ))}

                                            <li>
                                                <Link
                                                    href='/#simulador'
                                                    onClick={closeAll}
                                                    className='flex h-full flex-col justify-between gap-3 rounded-xl bg-ink p-4 text-primary-foreground transition-colors hover:bg-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                                                >
                                                    <span className='text-sm font-semibold'>
                                                        Não sabe qual escolher?
                                                    </span>

                                                    <span className='inline-flex items-center gap-1.5 text-xs font-semibold text-gold'>
                                                        Compare no simulador
                                                        <ArrowRight aria-hidden='true' className='size-4' />
                                                    </span>
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </li>

                        {mainNavigation.map(item => (
                            <li key={item.href}>
                                <Link href={item.href} onClick={closeAll} className={navLinkClassName}>
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href={ctaHref}
                        onClick={closeAll}
                        className={buttonClassName({ size: 'sm', className: 'hidden sm:inline-flex' })}
                    >
                        Receber proposta
                    </Link>

                    <button
                        type='button'
                        aria-expanded={isMenuOpen}
                        aria-controls='menu-celular'
                        onClick={() => setIsMenuOpen(open => !open)}
                        className='grid size-11 place-items-center rounded-full border border-border bg-card text-foreground transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden'
                    >
                        {isMenuOpen ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}

                        <span className='sr-only'>
                            {isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                        </span>
                    </button>
                </div>
            </div>

            {isMenuOpen && (
                <div id='menu-celular' className='border-t border-border bg-background lg:hidden'>
                    <nav
                        aria-label='Menu'
                        className='mx-auto max-h-[calc(100dvh-4.5rem)] max-w-7xl overflow-y-auto px-5 pb-6 pt-5 sm:px-8'
                    >
                        <p className='px-3 text-xs font-bold uppercase tracking-[0.2em] text-bronze'>
                            Consórcios
                        </p>

                        <ul className='mt-2 grid gap-1 sm:grid-cols-2'>
                            {consortiumCategories.map(category => (
                                <li key={category.slug}>
                                    <Link
                                        href={`/consorcio/${category.slug}`}
                                        onClick={closeAll}
                                        className='flex items-center gap-3 rounded-xl px-3 py-2.5 font-semibold transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                                    >
                                        <span className='grid size-10 shrink-0 place-items-center rounded-lg bg-sand text-bronze'>
                                            <ConsortiumIcon slug={category.slug} className='size-5' />
                                        </span>

                                        {category.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <ul className='mt-4 grid gap-1 border-t border-border pt-4'>
                            {mainNavigation.map(item => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        onClick={closeAll}
                                        className='block rounded-xl px-3 py-3 text-base font-semibold transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <Link
                            href={ctaHref}
                            onClick={closeAll}
                            className={buttonClassName({ size: 'lg', className: 'mt-5 w-full' })}
                        >
                            Receber uma proposta
                            <ArrowRight aria-hidden='true' className='size-5' />
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    )
}
